import { API_BASE, fetchWithRetry, DEFAULT_RETRY_DELAYS } from './http';

const DEFAULT_PREFIX = '/api';

function resolveApiPrefix() {
  const isBrowser = typeof window !== 'undefined' && typeof window.location !== 'undefined';
  if (isBrowser) {
    const host = window.location.hostname;
    if (host && host !== 'localhost' && host !== '127.0.0.1') {
      return DEFAULT_PREFIX;
    }
  }
  if (API_BASE && API_BASE !== DEFAULT_PREFIX) {
    return API_BASE;
  }
  return DEFAULT_PREFIX;
}

const API_PREFIX = resolveApiPrefix();
const API_ROOT = API_PREFIX.replace(/\/api$/i, '');

async function readBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (err) {
    return text;
  }
}

async function handleResponse(res) {
  const body = await readBody(res);

  if (res.ok) {
    return body;
  }

  if (res.status === 403) {
    const error = body && typeof body === 'object' ? body.error || body.message : null;
    return { ok: false, error: error || 'forbidden', status: res.status };
  }

  const message = typeof body === 'string' && body
    ? body
    : body?.error || body?.message;
  throw new Error(message || `Request failed with status ${res.status}`);
}

export async function fetchMonthSummary({ year, month, signal, onRetry, retryDelays = DEFAULT_RETRY_DELAYS } = {}) {
  const q = new URLSearchParams();
  if (year) q.set('year', String(year));
  if (month) q.set('month', String(month));
  const res = await fetchWithRetry('/shipments/month-summary', {
    base: API_PREFIX,
    params: q,
    credentials: 'include',
    signal,
    onRetry,
    retryDelays,
  });
  return handleResponse(res);
}

export async function fetchDayDevices({ date, limit = 500, signal, onRetry, retryDelays = DEFAULT_RETRY_DELAYS }) {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (limit) params.set('limit', String(limit));
  const res = await fetchWithRetry('/shipments/day-devices', {
    base: API_PREFIX,
    params,
    credentials: 'include',
    signal,
    onRetry,
    retryDelays,
  });
  return handleResponse(res);
}

export async function fetchRangeSummary({ from, to, granularity = 'month', breakdown = false, signal, onRetry, retryDelays = DEFAULT_RETRY_DELAYS }) {
  const params = new URLSearchParams();
  params.set('from', from);
  params.set('to', to);
  params.set('granularity', granularity);
  if (breakdown) params.set('breakdown', 'true');
  const res = await fetchWithRetry('/shipments/range-summary', {
    base: API_PREFIX,
    params,
    credentials: 'include',
    signal,
    onRetry,
    retryDelays,
  });
  return handleResponse(res);
}

export async function fetchShipmentsInsights({ from, to, granularity, points, total, breakdown, signal }) {
  const res = await fetchWithRetry('/shipments/ai-insights', {
    base: API_PREFIX,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to, granularity, points, total, breakdown: breakdown || null }),
    credentials: 'include',
    signal,
    retryDelays: [], // no retries — AI calls are expensive
  });
  return handleResponse(res);
}

export async function fetchShipmentsProjection({ from, to, granularity, points, total, horizonPeriods, breakdown, signal }) {
  const res = await fetchWithRetry('/shipments/ai-projection', {
    base: API_PREFIX,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to, granularity, points, total, horizonPeriods, breakdown: breakdown || null }),
    credentials: 'include',
    signal,
    retryDelays: [],
  });
  return handleResponse(res);
}

export async function warmBackend() {
  const warmupDelays = [200, 600, 1200];
  const endpoints = ['/healthz', '/readyz'];

  for (const endpoint of endpoints) {
    try {
      const response = await fetchWithRetry(endpoint, {
        base: API_ROOT,
        retryDelays: warmupDelays,
      });
      if (!response.ok) {
        console.warn(`[shipmentsApi] Warmup request to ${endpoint} failed with status ${response.status}`);
      }
    } catch (err) {
      console.warn(`[shipmentsApi] Warmup request to ${endpoint} failed`, err);
    }
  }
}
